import { Navigate, Route, Routes } from 'react-router-dom';
import { useEffect, type ReactNode } from 'react';
import { useAppDispatch, useAppSelector } from './hooks/redux';
import { isTokenExpired, logout } from './features/auth/authSlice';
import AdminShell from './components/AdminShell';
import LoginPage from './pages/Login';
import AdminHome from './pages/AdminHome';
import UsersPage from './pages/Users';
import UserDetailPage from './pages/Users/Detail';
import UserFormPage from './pages/Users/Form';
import CompaniesPage from './pages/Companies';
import CompanyFormPage from './pages/Companies/Form';
import PartnersPage from './pages/Partners';
import PartnerFormPage from './pages/Partners/Form';
import FeesPage from './pages/Fees';
import FeeFormPage from './pages/Fees/Form';
import PaymentsPage from './pages/Payments';
import PaymentFormPage from './pages/Payments/Form';
import DocumentsPage from './pages/Documents';
import DocumentCountryPage from './pages/Documents/Country';
import DocumentFormPage from './pages/Documents/Form';
import DocumentCountryFormPage from './pages/Documents/NewCountry';
import RatingsPage from './pages/Ratings';
import RatingFormPage from './pages/Ratings/Form';
import AiSettingsPage from './pages/AiSettings';
import AiModelFormPage from './pages/AiSettings/Form';

const EXPIRED = 'Your session has expired. Sign in again.';

function RequireAuth({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const token = useAppSelector((s) => s.auth.token);
  const expired = isTokenExpired(token);

  useEffect(() => {
    if (token && expired) dispatch(logout(EXPIRED));
  }, [token, expired, dispatch]);

  useEffect(() => {
    if (!token || expired) return;
    const exp = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp as number;
    const ms = exp * 1000 - Date.now();
    const timer = window.setTimeout(() => dispatch(logout(EXPIRED)), Math.min(ms, 2 ** 31 - 1));
    return () => window.clearTimeout(timer);
  }, [token, expired, dispatch]);

  if (!token || expired) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <AdminShell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<AdminHome />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/users/new" element={<UserFormPage />} />
        <Route path="/users/:id" element={<UserDetailPage />} />
        <Route path="/companies" element={<CompaniesPage />} />
        <Route path="/companies/new" element={<CompanyFormPage />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/partners/new" element={<PartnerFormPage />} />
        <Route path="/partners/:id" element={<PartnerFormPage />} />
        <Route path="/fees" element={<FeesPage />} />
        <Route path="/fees/new" element={<FeeFormPage />} />
        <Route path="/fees/:code" element={<FeeFormPage />} />
        <Route path="/payments" element={<PaymentsPage />} />
        <Route path="/payments/new" element={<PaymentFormPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/documents/new" element={<DocumentCountryFormPage />} />
        <Route path="/documents/:country" element={<DocumentCountryPage />} />
        <Route path="/documents/:country/new" element={<DocumentFormPage />} />
        <Route path="/documents/:country/:id" element={<DocumentFormPage />} />
        <Route path="/ratings" element={<RatingsPage />} />
        <Route path="/ratings/:country" element={<RatingFormPage />} />
        <Route path="/ai-settings" element={<AiSettingsPage />} />
        <Route path="/ai-settings/new" element={<AiModelFormPage />} />
        <Route path="/ai-settings/:id" element={<AiModelFormPage />} />
        <Route path="/remediation" element={<Navigate to="/" replace />} />
        <Route path="/countries" element={<Navigate to="/documents" replace />} />
        <Route path="/ai-config" element={<Navigate to="/ai-settings" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
